import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DomainAPI } from '../lib/api';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Skeleton } from '../components/ui/Skeleton';
import { TopBar } from '../components/TopBar';
import { Table, Thead, Tbody, Th, Tr, Td } from '../components/ui/Table';

export function DomainsPage() {
  const [dnsDialog, setDnsDialog] = useState<string | null>(null);

  const { data: localData, isLoading: localLoading } = useQuery({
    queryKey: ['domains-local'],
    queryFn: DomainAPI.local,
  });

  const { data: hostingerData, isLoading: hostingerLoading } = useQuery({
    queryKey: ['domains-hostinger'],
    queryFn: DomainAPI.hostinger,
  });

  const { data: dnsData } = useQuery({
    queryKey: ['dns', dnsDialog],
    queryFn: () => DomainAPI.dnsRecords(dnsDialog!),
    enabled: !!dnsDialog,
  });

  const localDomains: any[] = localData?.items ?? localData ?? [];
  const hostingerDomains: any[] = Array.isArray(hostingerData) ? hostingerData : hostingerData?.domains ?? [];
  const hostingerError = hostingerData?.error ?? null;

  return (
    <div>
      <TopBar title="Домени" />
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Nginx vhosts (локальні)</CardTitle>
          </CardHeader>
          {localLoading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : localDomains.length > 0 ? (
            <div className="space-y-2">
              {localDomains.map((d: any) => (
                <div key={d.serverName} className="flex items-center justify-between py-2 border-b border-tg-border last:border-0">
                  <div>
                    <div className="text-sm font-medium text-tg-text">{d.serverName}</div>
                    <div className="text-xs text-tg-text-secondary">
                      {d.ports?.join(', ') ?? '—'}
                    </div>
                  </div>
                  <Badge variant={d.ssl ? 'success' : 'muted'}>
                    {d.ssl ? 'SSL' : 'HTTP'}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-tg-text-secondary">Немає vhosts</p>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Hostinger</CardTitle>
          </CardHeader>
          {hostingerLoading ? (
            <div className="space-y-2">
              {[1,2].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : hostingerError ? (
            <p className="text-sm text-tg-text-secondary">
              Hostinger API key не налаштований
            </p>
          ) : hostingerDomains.length > 0 ? (
            <div className="space-y-2">
              {hostingerDomains.map((d: any) => (
                <div key={d.domain} className="flex items-center justify-between py-2 border-b border-tg-border last:border-0">
                  <div className="text-sm font-medium text-tg-text">{d.domain}</div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDnsDialog(d.domain)}
                  >
                    DNS записи
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-tg-text-secondary">Домени не знайдені</p>
          )}
        </Card>
      </div>

      <Dialog
        open={!!dnsDialog}
        onClose={() => setDnsDialog(null)}
        title={`DNS записи: ${dnsDialog}`}
      >
        {dnsData ? (
          <Table>
            <Thead>
              <Tr>
                <Th>Тип</Th>
                <Th>Назва</Th>
                <Th>Значення</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(Array.isArray(dnsData) ? dnsData : dnsData.records ?? []).map((r: any, i: number) => (
                <Tr key={i}>
                  <Td><Badge variant="muted">{r.type}</Badge></Td>
                  <Td className="font-mono text-xs">{r.name}</Td>
                  <Td className="font-mono text-xs break-all">{r.value || r.content}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          <p className="text-sm text-tg-text-secondary">Завантаження...</p>
        )}
      </Dialog>
    </div>
  );
}
